import { MENU_IMPORT_TARGET } from '@/domain/assistant/assistant-nav-targets';
import type { IAssistantNavTarget } from '@/domain/assistant/assistant-nav-targets';
import type {
  IGhCalloutCard,
  IGhNavigateAction,
  IGhNavigateTarget,
} from '../gpthelp-host-contract';
import {
  bidiIsolateParams,
  cardTextLength,
  clampText,
  fitText,
  joinSentences,
} from './card-text';
import type { ICardFormat } from './card-text';
import { permissionLockCard, planLockCard, planName } from './lock-cards';

/** Why the member does something on the page instead of the assistant. */
export type TNavHandoffReason =
  | 'irreversible'
  | 'money'
  | 'legal'
  | 'notSupported';

/** What the member does on the page: a handoff card lists its steps. */
export const NAV_TASKS = [
  'deleteProduct',
  'deleteCategory',
  'deleteOptionGroup',
  'productPhoto',
  'translations',
  'allergens',
  'vat',
  'printing',
  'storeSettings',
  'team',
] as const;
export type TNavTask = typeof NAV_TASKS[number];

/** The Menú editors a card opens: a category's, a new product, a new category. */
export const NAV_EDITORS = ['category', 'newProduct', 'newCategory'] as const;
export type TNavEditor = typeof NAV_EDITORS[number];

/** Tasks done in a product's editor. */
const PRODUCT_TASKS: readonly TNavTask[] = [
  'deleteProduct',
  'productPhoto',
  'translations',
  'allergens',
  'vat',
  'printing',
];

/** Tasks whose steps start on the Menú page. */
export const MENU_TASKS: readonly TNavTask[] = [
  ...PRODUCT_TASKS,
  'deleteCategory',
];

/** Tasks a category's own editor also does. */
const CATEGORY_EDITOR_TASKS: readonly TNavTask[] = [
  'deleteCategory',
  'translations',
  'printing',
];

/**
 * Whether a card may open `editor` for `task`, so the button and the steps
 * agree: with a task, only a category's editor, and only for a task done
 * there; any editor without a task.
 */
export function editorFitsTask(
  editor: TNavEditor,
  task: TNavTask | null
): boolean {
  return (
    task === null ||
    (editor === 'category' && CATEGORY_EDITOR_TASKS.includes(task))
  );
}

/** Steps a task card lists. */
const TASK_STEPS = [1, 2, 3];

export interface INavCardInput {
  id: string;
  target: IAssistantNavTarget;
  /** The page title in the admin locale. */
  pageTitle: string;
  /** Its drawer group's title in the admin locale; null for a top-level page. */
  groupTitle: string | null;
  /** A Menú product whose editor opens (its name labels the button). */
  item: { id: number; name: string } | null;
  /** A Menú category to open (its name labels the button). */
  category: { id: number; name: string } | null;
  /**
   * The Menú editor to open: `category`'s own ('category'), a new product
   * in `category` ('newProduct') or a new category ('newCategory').
   */
  editor: TNavEditor | null;
  /** The menu of the product or category (null = the base menu). */
  menuId: number | null;
  /** Products to flash on the Menú page. */
  highlightItemIds: number[];
  /** The handoff task whose steps the card lists. */
  task: TNavTask | null;
}

export interface INavHandoffInput extends INavCardInput {
  reason: TNavHandoffReason | null;
  /**
   * The product or category the member could hide instead of what cannot be
   * undone (only when the assistant can prepare hiding it).
   */
  hideName: string | null;
  /**
   * The member may not edit the menu: the lock card says so and whom to ask,
   * without steps naming buttons they do not have or a button to the page.
   */
  catalogLocked: boolean;
}

/** Characters of a button label (host contract §3.3). */
const LABEL_MAX = 40;
/** Characters of a callout text (host contract §3.3). */
const TEXT_MAX = 600;
/** A blank line: the paragraph break of a callout text in the widget. */
const PARAGRAPH_BREAK = '\n\n';

/**
 * The editor the button opens when it opens no product: a category's or a
 * new product's needs the category; null for a plain page or category.
 */
function menuEditor(input: INavCardInput): TNavEditor | null {
  if (input.item || input.editor === null) return null;
  if (input.editor === 'newCategory') return input.editor;
  return input.category ? input.editor : null;
}

function navigateTarget(input: INavCardInput): IGhNavigateTarget {
  const target: IGhNavigateTarget = { page: input.target.key };
  const editor = menuEditor(input);
  const params: Record<string, string> = {};
  if (input.item) params.itemId = String(input.item.id);
  else if (input.category && editor !== 'newCategory')
    params.categoryId = String(input.category.id);
  if (editor) params.editor = editor;
  if (input.menuId !== null) params.menuId = String(input.menuId);
  if (Object.keys(params).length) target.params = params;
  if (input.highlightItemIds.length)
    target.highlight = input.highlightItemIds.map(String);
  return target;
}

/**
 * «Abrir {product}», «Editar {category}», «Nuevo producto en {category}»,
 * «Crear una categoría», «Abrir {category} en el Menú» or «Abrir {page}».
 */
function openLabel(input: INavCardInput, fmt: ICardFormat): string {
  const editor = menuEditor(input);
  if (input.item)
    return fitText(
      fmt,
      'assistant.nav.openItem',
      { name: input.item.name },
      'name',
      LABEL_MAX
    );
  if (editor === 'newCategory')
    return clampText(fmt.t('assistant.nav.newCategory'), LABEL_MAX);
  if (input.category) {
    const key =
      editor === 'category'
        ? 'assistant.nav.editCategory'
        : editor === 'newProduct'
        ? 'assistant.nav.newProduct'
        : 'assistant.nav.openCategory';
    return fitText(fmt, key, { name: input.category.name }, 'name', LABEL_MAX);
  }
  return fitText(
    fmt,
    'assistant.nav.open',
    { page: input.pageTitle },
    'page',
    LABEL_MAX
  );
}

function openAction(input: INavCardInput, fmt: ICardFormat): IGhNavigateAction {
  return {
    id: 'open',
    kind: 'navigate',
    label: openLabel(input, fmt),
    icon: 'openInNew',
    style: 'outline',
    target: navigateTarget(input),
  };
}

/**
 * The i18n key of a task's step. Step 1 follows what the button opens: the
 * category's editor, or the Menú itself when a product task has no product
 * to open. A category's printers are a section of its editor, not a part of
 * «Más ajustes» as a product's are.
 */
function stepKey(input: INavCardInput, task: TNavTask, n: number): string {
  const categoryEditor = menuEditor(input) === 'category';
  if (n === 1 && categoryEditor) return 'assistant.nav.steps.openCategory';
  if (n === 1 && PRODUCT_TASKS.includes(task) && !input.item)
    return 'assistant.nav.steps.findProduct';
  if (n === 2 && task === 'printing' && categoryEditor)
    return 'assistant.nav.steps.printing.category.2';
  return `assistant.nav.steps.${task}.${n}`;
}

/**
 * «1. Abra el producto con el botón de abajo.»: the task's steps, naming the
 * buttons and sections with the screen's own labels, so they match it in
 * every language.
 */
function taskSteps(input: INavCardInput, fmt: ICardFormat): string[] {
  const { task } = input;
  if (!task) return [];
  const labels = {
    more: fmt.t('menuPage.editor.moreSettings'),
    invoicing: fmt.t('menuPage.invoicing.title'),
    translations: fmt.t('menuPage.translations.title'),
    printing: fmt.t('menuPage.printing.title'),
    allergens: fmt.t('menuPage.editor.allergens'),
    deleteProduct: fmt.t('menuPage.editor.deleteProduct'),
    deleteCategory: fmt.t('menuPage.categoryMenu.delete'),
    deleteGroup: fmt.t('optionEditor.delete'),
    save: fmt.t('save'),
    page: input.pageTitle,
  };
  return TASK_STEPS.map((n) => {
    // Isolated like any value, so «1.» stays with its step in Arabic and Urdu.
    const { n: step } = bidiIsolateParams(fmt.locale, { n: fmt.number(n) });
    return `${step}. ${fmt.t(stepKey(input, task, n), labels)}`;
  });
}

/** Paragraphs a blank line apart; empty ones are left out. */
function paragraphs(parts: string[]): string {
  return parts.filter((part) => part !== '').join(PARAGRAPH_BREAK);
}

/**
 * Why (and whom to ask), each step as its own paragraph, and the offer to
 * hide instead, within the callout text's 600 characters. Steps are never
 * cut: the first one that does not fit is left out with every one after it.
 * The widget draws a row without a value as a centered «y N más» footer, so
 * the steps are paragraphs of the text, which it aligns to the start.
 */
function stepsText(lead: string, steps: string[], hide: string): string {
  const kept: string[] = [];
  for (const step of steps) {
    if (cardTextLength(paragraphs([lead, ...kept, step, hide])) > TEXT_MAX)
      break;
    kept.push(step);
  }
  return paragraphs([lead, ...kept, hide]);
}

/**
 * «Estadísticas › Pedidos»: the page inside its drawer group, since several
 * groups have a «Pedidos» or a «Configuración»; a top-level page alone.
 */
function pageHeading(
  input: Pick<INavCardInput, 'pageTitle' | 'groupTitle'>,
  fmt: ICardFormat
): string {
  if (!input.groupTitle) return clampText(input.pageTitle, 160);
  const { group, page } = bidiIsolateParams(fmt.locale, {
    group: input.groupTitle,
    page: input.pageTitle,
  });
  return clampText(`${group} › ${page}`, 160);
}

/** The page, with a button that opens it (the assistant never navigates by itself). */
export function navOpenCard(
  input: INavCardInput,
  fmt: ICardFormat
): IGhCalloutCard {
  return {
    id: input.id,
    v: 1,
    type: 'callout',
    tone: 'neutral',
    title: pageHeading(input, fmt),
    actions: [openAction(input, fmt)],
  };
}

/**
 * «Esto lo hace usted desde la página»: why the member does it on the page,
 * the steps of the task, and the button that opens it; for something that
 * cannot be undone, the offer to hide the product or category instead. A
 * member who may not edit the menu gets the lock card: whom to ask and, when
 * the assistant can hide it instead (its chip offers it), that hiding is
 * allowed, without a button (the page would not let them do it either).
 */
export function navHandoffCard(
  input: INavHandoffInput,
  fmt: ICardFormat
): IGhCalloutCard {
  if (input.catalogLocked)
    return permissionLockCard(
      {
        id: input.id,
        titleKey: 'assistant.lock.edit',
        canToggle: input.hideName !== null,
      },
      fmt
    );
  const hide = input.hideName
    ? fitText(
        fmt,
        'assistant.nav.hideInstead',
        { name: input.hideName },
        'name',
        240
      )
    : '';
  const card: IGhCalloutCard = {
    id: input.id,
    v: 1,
    type: 'callout',
    tone: 'info',
    title: clampText(fmt.t('assistant.nav.handoffTitle'), 160),
    actions: [openAction(input, fmt)],
  };
  const lead = input.reason
    ? fmt.t(`assistant.nav.reason.${input.reason}`)
    : '';
  const steps = taskSteps(input, fmt);
  const text = steps.length
    ? stepsText(lead, steps, hide)
    : joinSentences(fmt.locale, [lead, hide]);
  if (text) card.text = clampText(text, TEXT_MAX);
  return card;
}

/** A page the store's plan does not include: «Disponible en el plan Pro». */
export function navLockedCard(
  input: Pick<INavCardInput, 'id' | 'target' | 'pageTitle' | 'groupTitle'>,
  canSeePlans: boolean,
  fmt: ICardFormat
): IGhCalloutCard {
  return planLockCard(
    {
      id: input.id,
      title: pageHeading(input, fmt),
      text: fmt.t('assistant.lock.plan', {
        plan: planName(input.target.minTier ?? 1),
      }),
      canSeePlans,
    },
    fmt
  );
}

/**
 * «Actualizar desde fotos»: the menu is updated from photos in the menu
 * uploader, which does not support stores with several menus yet.
 */
export function menuImportCard(
  id: string,
  multipleMenus: boolean,
  fmt: ICardFormat
): IGhCalloutCard {
  const card: IGhCalloutCard = {
    id,
    v: 1,
    type: 'callout',
    tone: 'neutral',
    icon: 'camera',
    title: clampText(fmt.t('assistant.nav.photoTitle'), 160),
    text: clampText(
      fmt.t(
        multipleMenus ? 'assistant.nav.multiMenu' : 'assistant.nav.photoText'
      ),
      600
    ),
  };
  if (!multipleMenus)
    card.actions = [
      {
        id: 'open',
        kind: 'navigate',
        label: clampText(fmt.t('assistant.nav.photoOpen'), LABEL_MAX),
        icon: 'openInNew',
        style: 'outline',
        target: { page: MENU_IMPORT_TARGET },
      },
    ];
  return card;
}
